import { useCallback, useMemo } from 'react';

import type { ReactElement, ReactNode } from 'react';
import type { EntitlementGrant, RbacAction } from '@plerionhq/types-rbac';

import { useEntitlements } from './use-entitlements';
import { useIntegrationGroupMembers } from './use-integration-group-members';

const GROUP_REF_PREFIX = 'group:';

/**
 * True iff `grant` authorizes `action` (optionally on `resource`). Mirrors the
 * backend authorizer's evaluate-route logic so the UI never disagrees with it.
 */
const grantMatches = (
  grant: EntitlementGrant,
  action: RbacAction,
  resource?: string,
  groupMembers: Record<string, string[]> = {},
): boolean => {
  if (grant.action !== action) {
    return false;
  }
  // resources === null → action has no resource dimension; applies tenant-wide.
  if (grant.resources == null) {
    return true;
  }
  if (grant.resources === '*') {
    return true;
  }
  // No resource asked for → "holds it on at least one resource".
  if (resource === undefined) {
    return grant.resources.length > 0;
  }
  // A group whose membership has not loaded contributes nothing, so an
  // unresolved reference answers false rather than true.
  return grant.resources.some((granted) =>
    granted.startsWith(GROUP_REF_PREFIX)
      ? (groupMembers[granted.slice(GROUP_REF_PREFIX.length)] ?? []).includes(
          resource,
        )
      : granted === resource,
  );
};

export type UseAccessControl = {
  /** Org admin → always true; fail-closed (false) while loading or on error. */
  can: (check: { action: RbacAction; resource?: string }) => boolean;
  /**
   * True only when the grant for `action` is not narrowed to named resources.
   *
   * A different question from `can`, which is true as soon as the action is
   * held on one resource. Use it for a view that covers the whole tenant
   * and cannot be filtered: showing it to a caller who can see three of forty
   * integrations states a total that is not theirs.
   */
  canAcrossTenant: (check: { action: RbacAction }) => boolean;
  isOrgAdmin: boolean;
  /** Entitlements still resolving. */
  isLoading: boolean;
};

/**
 * RBAC access checker backed by the current user's resolved entitlements.
 * Single-action by design (the backend action-map is one-action-per-endpoint).
 */
export const useAccessControl = (): UseAccessControl => {
  const { data, isLoading, isError } = useEntitlements();
  const isOrgAdmin = data?.isOrgAdmin ?? false;
  // An org admin answers true before any grant is read, so the group list
  // would be fetched and never looked at.
  const { data: groupMembers } = useIntegrationGroupMembers({
    enabled: !isOrgAdmin,
  });

  // Identity changes exactly when a decision could change, so callers can put
  // `can` in a dependency array and re-evaluate when entitlements arrive or are
  // refetched — depending on `isLoading` alone would miss a data-only update.
  const can = useCallback(
    ({
      action,
      resource,
    }: {
      action: RbacAction;
      resource?: string;
    }): boolean => {
      if (isLoading || isError || !data) {
        return false;
      }
      if (data.isOrgAdmin) {
        return true;
      }
      return data.grants.some((grant) =>
        grantMatches(grant, action, resource, groupMembers),
      );
    },
    [data, isLoading, isError, groupMembers],
  );

  const canAcrossTenant = useCallback(
    ({ action }: { action: RbacAction }): boolean => {
      if (isLoading || isError || !data) {
        return false;
      }
      if (data.isOrgAdmin) {
        return true;
      }
      return data.grants.some((grant) => {
        if (grant.action !== action) {
          return false;
        }
        // null: the action has no resource dimension, so holding it is
        // tenant-wide by definition. '*': held on every resource.
        return grant.resources == null || grant.resources === '*';
      });
    },
    [data, isLoading, isError],
  );

  return useMemo(
    () => ({ can, canAcrossTenant, isOrgAdmin, isLoading }),
    [can, canAcrossTenant, isOrgAdmin, isLoading],
  );
};

export type AccessControlProps = {
  action: RbacAction;
  resource?: string;
  children: ReactNode;
  /** Rendered when the check fails. Default: nothing. */
  fallback?: ReactNode;
  /** Rendered while entitlements resolve. Default: nothing. */
  loading?: ReactNode;
};

/**
 * Declarative RBAC gate. Renders `children` iff the current user may perform
 * `action` (optionally on `resource`), else `fallback`. RBAC-only replacement
 * for the per-module ProtectedComponent copies.
 */
export const AccessControl = ({
  action,
  resource,
  children,
  fallback = null,
  loading = null,
}: AccessControlProps): ReactElement => {
  const { can, isLoading } = useAccessControl();
  if (isLoading) {
    return <>{loading}</>;
  }
  return <>{can({ action, resource }) ? children : fallback}</>;
};
